import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SrIcon } from "@/components/ui/SrIcon";

interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** URL to a PDF, image, or other file. Blob-URL, Supabase public/signed URL, or external. */
  url: string;
  /** Optional. If not provided, MIME is inferred from URL extension. */
  mimeType?: string;
  /** Optional file name; used for the "Last ned"-fallback filename. */
  fileName?: string;
  /** Title shown above the preview. */
  title: string;
}

type FileKind = "pdf" | "image" | "unsupported";

function detectKind(url: string, mimeType?: string): FileKind {
  if (mimeType) {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
    return "unsupported";
  }
  const lower = url.toLowerCase().split("?")[0];
  if (lower.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(lower)) return "image";
  return "unsupported";
}

export default function FilePreviewDialog({
  open,
  onOpenChange,
  url,
  mimeType,
  fileName,
  title,
}: FilePreviewDialogProps) {
  const kind = detectKind(url, mimeType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader>
          {/* Title + "Åpne i ny fane" stacked left-side så X-knappen til høyre ikke
              krasjer med lenken. */}
          <div className="flex items-center gap-3 pr-8 min-w-0">
            <DialogTitle className="truncate">{title}</DialogTitle>
            {/* Ingen download-attributt: lenken skal ÅPNE i ny fane (inline),
                ikke laste ned. Dette er workarounden når en URL har
                Content-Disposition: attachment (PdfIframe trigger nedlasting). */}
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
            >
              <SrIcon name="external-link" className="w-3 h-3" /> Åpne i ny fane
            </a>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg overflow-hidden border bg-muted/30">
          {kind === "pdf" && open && <PdfIframe url={url} fileName={fileName} />}
          {kind === "image" && open && <ImageZoom url={url} title={title} />}
          {kind === "unsupported" && (
            <UnsupportedFallback url={url} fileName={fileName} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── PDF ───────────────────────────────────────────────────────────────── */

/**
 * Bruker iframe med browserens native PDF-viewer. Det gir innebygd verktøylinje
 * (zoom, page-nav, søk, print) som Chrome/Edge/Firefox/Safari leverer.
 *
 * Caveat: hvis server returnerer Content-Disposition: attachment (typisk
 * Monday-eksporterte URLer), vil iframe trigge nedlasting i stedet for inline
 * visning. "Åpne i ny fane"-lenken i header er da workaround. Egen issue
 * dekker eventuell proxy-løsning for slike URLer hvis det blir reelt problem.
 */
function PdfIframe({ url, fileName }: { url: string; fileName?: string }) {
  return (
    <iframe
      src={url}
      title={fileName ?? "PDF-forhåndsvisning"}
      className="w-full h-full"
      style={{ border: "none" }}
    />
  );
}

/* ── Image with pan + zoom ─────────────────────────────────────────────── */

function ImageZoom({ url, title }: { url: string; title: string }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragging = useRef<{ x: number; y: number } | null>(null);
  const [errored, setErrored] = useState(false);

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(0.25, Math.min(8, scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    setScale(next);
  };

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = { x: e.clientX - tx, y: e.clientY - ty };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return;
    setTx(e.clientX - dragging.current.x);
    setTy(e.clientY - dragging.current.y);
  };
  const onMouseUp = () => {
    dragging.current = null;
  };

  if (errored) {
    return <UnsupportedFallback url={url} />;
  }

  return (
    <div
      className="relative h-full overflow-hidden bg-muted/50"
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      style={{ cursor: dragging.current ? "grabbing" : "grab" }}
    >
      <img
        src={url}
        alt={title}
        onError={() => setErrored(true)}
        draggable={false}
        className="absolute top-1/2 left-1/2 select-none"
        style={{
          transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center",
          maxWidth: "none",
        }}
      />
      <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-background/90 px-2 py-1 text-xs shadow">
        <button
          type="button"
          onClick={() => setScale((s) => Math.max(0.25, s / 1.25))}
          className="px-1.5 hover:text-primary"
          aria-label="Zoom ut"
        >
          −
        </button>
        <span className="tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          onClick={() => setScale((s) => Math.min(8, s * 1.25))}
          className="px-1.5 hover:text-primary"
          aria-label="Zoom inn"
        >
          +
        </button>
        <button
          type="button"
          onClick={reset}
          className="px-1.5 hover:text-primary ml-1"
          aria-label="Tilbakestill"
        >
          ↺
        </button>
      </div>
    </div>
  );
}

/* ── Unsupported fallback ──────────────────────────────────────────────── */

function UnsupportedFallback({ url, fileName }: { url: string; fileName?: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
      <SrIcon name="file" className="w-8 h-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground max-w-md">
        Kan ikke forhåndsvises i nettleseren. Last ned filen for å åpne den i et eget program.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        download={fileName}
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <SrIcon name="download" className="w-4 h-4" /> Last ned
      </a>
    </div>
  );
}
